import React, { useEffect, useState } from "react";
import {
  ShieldCheck, LayoutDashboard, ShoppingBag, Megaphone, KeyRound, Package,
  Boxes, LayoutTemplate, Globe, Wallet, Plug, Bot, Loader2, Check, X, Trash2,
  Plus, Send, Edit3, Power, Sparkles, Layers, Zap, CheckCircle2, AlertCircle, Hash,
  FolderOpen, Download, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const PIN_KEY = "aql4_admin_pin";
const TABS = [
  { id: "overview", label: "نظرة عامة", Icon: LayoutDashboard },
  { id: "orders", label: "الطلبات", Icon: ShoppingBag },
  { id: "ads", label: "الإعلانات", Icon: Megaphone },
  { id: "keys", label: "المفاتيح (IQL)", Icon: KeyRound },
  { id: "codes", label: "قاعدة الرموز", Icon: Hash },
  { id: "packages", label: "الباقات", Icon: Package },
  { id: "services", label: "الخدمات الذكية", Icon: Boxes },
  { id: "pages", label: "منشئ الأقسام", Icon: LayoutTemplate },
  { id: "categories", label: "تصنيفات الموقع", Icon: Layers },
  { id: "domains", label: "النطاقات", Icon: Globe },
  { id: "sites", label: "المواقع المنشورة", Icon: LayoutTemplate },
  { id: "agents", label: "الوكلاء", Icon: Bot },
  { id: "marketing", label: "التسويق والناشر", Icon: Megaphone },
  { id: "aistudio", label: "استوديو الذكاء", Icon: Sparkles },
  { id: "vault", label: "الربط والمفاتيح", Icon: KeyRound },
  { id: "wallet", label: "المحافظ", Icon: Wallet },
  { id: "integrations", label: "التكاملات", Icon: Plug },
  { id: "aiengines", label: "محرّكات الذكاء", Icon: Zap },
  { id: "ai", label: "الذكاء الإداري", Icon: Bot },
  { id: "buildprojects", label: "مشاريع البناء", Icon: FolderOpen },
  { id: "security", label: "أمان الإدارة", Icon: ShieldCheck },
];

const inp = "border-white/10 bg-white/5 text-white text-right";

function Login({ onOk }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try { await api.adminLogin(pin); localStorage.setItem(PIN_KEY, pin); onOk(pin); }
    catch (e) { toast.error("رمز الإدارة غير صحيح"); }
    setLoading(false);
  };
  return (
    <div className="flex min-h-screen items-center justify-center px-5">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.02] p-8 text-center">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl brand-gradient text-[#052027]"><ShieldCheck className="h-7 w-7" /></span>
        <h1 className="mt-4 font-display text-2xl font-extrabold text-white">دخول الإدارة</h1>
        <p className="mt-1 text-sm text-muted-foreground">إدارة محمية لعمليات المنصة المصرح بها.</p>
        <Input value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          type="password" placeholder="رمز الإدارة" className="mt-5 border-white/10 bg-white/5 text-center text-white" dir="ltr" />
        <Button onClick={submit} disabled={loading} className="btn-cyan mt-4 h-11 w-full gap-2 rounded-xl font-semibold hover:bg-transparent">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} دخول
        </Button>
      </div>
    </div>
  );
}

const Section = ({ title, children, action }) => (
  <div>
    <div className="mb-4 flex items-center justify-between">
      <h2 className="font-display text-xl font-bold text-white">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

const Row = ({ children }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">{children}</div>
);

export default function Admin() {
  const [pin, setPin] = useState(localStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");

  useEffect(() => {
    if (pin) api.adminOverview(pin).then(() => setAuthed(true)).catch(() => { setAuthed(false); });
  }, [pin]);

  if (!authed) return <main className="pt-16"><Login onOk={(p) => { setPin(p); setAuthed(true); }} /></main>;

  return (
    <main className="relative min-h-screen pt-24">
      <div className="mx-auto max-w-7xl px-5 py-6 lg:px-8">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-cyan" />
          <h1 className="font-display text-2xl font-extrabold text-white">لوحة الإدارة</h1>
          <span className="rounded-full bg-cyan/10 px-2 py-0.5 text-xs text-cyan">إدارة محمية</span>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[220px_1fr]">
          <aside className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
            {TABS.map(({ id, label, Icon }) => (
              <button key={id} onClick={() => setTab(id)}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${tab === id ? "bg-cyan/10 text-cyan" : "text-muted-foreground hover:bg-white/5 hover:text-white"}`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </aside>

          <div className="min-h-[60vh] rounded-2xl border border-white/10 bg-white/[0.01] p-5 lg:p-6">
            {tab === "overview" && <Overview pin={pin} />}
            {tab === "orders" && <Orders pin={pin} />}
            {tab === "ads" && <Ads pin={pin} />}
            {tab === "keys" && <Keys pin={pin} />}
            {tab === "codes" && <CodesTab pin={pin} />}
            {tab === "packages" && <Packages pin={pin} />}
            {tab === "services" && <ServicesTab pin={pin} />}
            {tab === "pages" && <Pages pin={pin} />}
            {tab === "categories" && <CategoriesTab pin={pin} />}
            {tab === "domains" && <Domains pin={pin} />}
            {tab === "sites" && <SitesTab pin={pin} />}
            {tab === "agents" && <AgentsTab pin={pin} />}
            {tab === "marketing" && <MarketingTab pin={pin} />}
            {tab === "aistudio" && <AIStudioTab pin={pin} />}
            {tab === "vault" && <VaultTab pin={pin} />}
            {tab === "wallet" && <WalletTab pin={pin} />}
            {tab === "integrations" && <Integrations pin={pin} />}
            {tab === "aiengines" && <AIEnginesTab pin={pin} />}
            {tab === "ai" && <AdminAI pin={pin} />}
            {tab === "buildprojects" && <AdminBuildProjects pin={pin} />}
            {tab === "security" && <AdminSecurity onPinChanged={(nextPin) => { localStorage.setItem(PIN_KEY, nextPin); setPin(nextPin); }} />}
          </div>
        </div>
      </div>
    </main>
  );
}

function AdminSecurity({ onPinChanged }) {
  const [form, setForm] = useState({ current_pin: "", new_pin: "", confirm_pin: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (form.new_pin.length < 8) { toast.error("استخدم 8 أحرف على الأقل"); return; }
    setSaving(true);
    try {
      const result = await api.adminChangePin(form);
      onPinChanged(form.new_pin);
      setForm({ current_pin: "", new_pin: "", confirm_pin: "" });
      toast.success(result.message);
    } catch (error) { toast.error(error?.response?.data?.detail || "تعذر تغيير رمز الإدارة"); }
    setSaving(false);
  };
  return (
    <Section title="أمان الإدارة">
      <div className="mb-5 space-y-2 text-sm leading-6 text-muted-foreground">
        <p>PIN الإدارة يُخزن كبصمة مملحة. يرفض الخادم الدخول إذا لم يُضبط PIN، ويحدّ من محاولات الدخول وطلبات الذكاء والدعم، ويرسل PIN من الواجهة في ترويسة بدل عنوان URL.</p>
        <p>هذه ضوابط تطبيقية وليست ضمانًا ضد كل هجوم. استخدم HTTPS، ومصادقة متعددة العوامل أو Cloudflare Access، وWAF، ونسخًا احتياطية واختبارات استعادة على مزود الإنتاج.</p>
      </div>
      <div className="grid max-w-xl gap-3">
        <div><Label className="text-xs text-white">رمز الإدارة الحالي</Label><Input type="password" autoComplete="current-password" value={form.current_pin} onChange={(e) => setForm({ ...form, current_pin: e.target.value })} className={"mt-1 " + inp} dir="ltr" /></div>
        <div><Label className="text-xs text-white">الرمز الجديد (8 أحرف على الأقل)</Label><Input type="password" autoComplete="new-password" value={form.new_pin} onChange={(e) => setForm({ ...form, new_pin: e.target.value })} className={"mt-1 " + inp} dir="ltr" /></div>
        <div><Label className="text-xs text-white">تأكيد الرمز الجديد</Label><Input type="password" autoComplete="new-password" value={form.confirm_pin} onChange={(e) => setForm({ ...form, confirm_pin: e.target.value })} className={"mt-1 " + inp} dir="ltr" /></div>
        <Button onClick={save} disabled={saving || !form.current_pin || !form.new_pin || !form.confirm_pin} className="btn-cyan w-fit gap-2 rounded-lg px-5 font-semibold hover:bg-transparent">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} حفظ الرمز الجديد</Button>
      </div>
    </Section>
  );
}

function Overview({ pin }) {
  const [d, setD] = useState(null);
  const [c, setC] = useState({});
  useEffect(() => { api.adminOverview(pin).then((x) => { setD(x); setC(x.counters); }); }, [pin]);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-cyan" />;
  const cards = [
    { l: "الإعلانات", v: d.ads }, { l: "الطلبات", v: d.orders }, { l: "طلبات معلّقة", v: d.pending_orders },
    { l: "المفاتيح", v: d.keys }, { l: "المحافظ", v: d.wallets }, { l: "الصفحات", v: d.pages },
  ];
  return (
    <Section title="نظرة عامة">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        {cards.map((k) => (
          <div key={k.l} className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-center">
            <div className="font-display text-3xl font-bold text-cyan">{k.v}</div>
            <div className="mt-1 text-xs text-muted-foreground">{k.l}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-3 font-semibold text-white">تعديل عدّادات الواجهة</h3>
        <div className="grid grid-cols-3 gap-3">
          {[["ads_approved", "إعلان معتمد"], ["requests_registered", "طلب مسجل"], ["requests_completed", "طلب مكتمل"]].map(([k, l]) => (
            <div key={k}><Label className="text-xs text-white">{l}</Label>
              <Input type="number" value={c[k] ?? 0} onChange={(e) => setC({ ...c, [k]: parseInt(e.target.value || 0) })} className={"mt-1 " + inp} /></div>
          ))}
        </div>
        <Button onClick={async () => { await api.adminPutCounters(c, pin).then(() => toast.success("تم الحفظ")).catch(() => toast.error("خطأ")); }}
          className="btn-cyan mt-3 rounded-lg px-5 font-semibold hover:bg-transparent">حفظ</Button>
      </div>
      <div className="mt-6">
        <h3 className="mb-2 font-semibold text-white">أحدث الطلبات</h3>
        <div className="space-y-2">
          {d.recent_orders.map((o) => (
            <Row key={o.id}><span className="text-sm text-white">{o.kind} · {o.amount} ريال</span>
              <span className="font-en text-xs text-muted-foreground" dir="ltr">{o.reference}</span>
              <span className={`text-xs ${o.status === "paid" ? "text-emerald-400" : "text-gold"}`}>{o.status}</span></Row>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Orders({ pin }) {
  const [list, setList] = useState([]);
  const load = () => api.adminOrders(pin).then(setList);
  useEffect(() => { load(); }, []);
  return (
    <Section title="الطلبات">
      <div className="space-y-2">
        {list.map((o) => (
          <Row key={o.id}>
            <div><p className="text-sm text-white">{o.name} · {o.kind}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">{o.reference} · {o.email}</p></div>
            <span className="text-sm font-bold text-gold">{o.amount} ريال</span>
            <span className={`rounded-md px-2 py-0.5 text-xs ${o.status === "paid" ? "bg-emerald-400/10 text-emerald-400" : o.status === "rejected" ? "bg-rose-400/10 text-rose-400" : "bg-amber-400/10 text-gold"}`}>{o.status}</span>
            {o.key_code && <span className="font-en text-xs text-cyan" dir="ltr">{o.key_code}</span>}
            {o.status === "pending" && (
              <div className="flex gap-2">
                <Button onClick={() => api.adminConfirmOrder(o.id, pin).then(() => { toast.success("تم الاعتماد"); load(); })} className="btn-cyan h-8 gap-1 rounded-lg px-3 text-xs font-semibold hover:bg-transparent"><Check className="h-3 w-3" /> اعتماد</Button>
                <Button onClick={() => api.adminRejectOrder(o.id, pin).then(() => load())} variant="outline" className="h-8 gap-1 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><X className="h-3 w-3" /> رفض</Button>
              </div>
            )}
          </Row>
        ))}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد طلبات.</p>}
      </div>
    </Section>
  );
}

function Ads({ pin }) {
  const [list, setList] = useState([]);
  const [reviewing, setReviewing] = useState("");
  const load = () => api.adminAds(pin).then(setList);
  useEffect(() => { load(); }, []);
  const reviewAd = async (adId) => {
    setReviewing(adId);
    try {
      const review = await api.adminReviewAd(adId, pin);
      setList((current) => current.map((ad) => ad.id === adId ? { ...ad, ai_review: review } : ad));
      toast.success(`اكتملت المراجعة المساعدة · الخطر ${review.risk || "غير محدد"}`);
    } catch (error) { toast.error(error?.response?.data?.detail || "تعذرت مراجعة الإعلان"); }
    setReviewing("");
  };
  return (
    <Section title="الإعلانات المدفوعة والمراجعة">
      <div className="space-y-2">
        {list.map((a) => (
          <Row key={a.id}>
            <div className="min-w-52"><p className="text-sm text-white">{a.title}</p><p className="text-xs text-muted-foreground">{a.city} · {a.section} · {a.products?.length || 0} منتجات · {a.listing_fee ? `${a.listing_fee} ريال` : "قديم"}</p><p className="font-en text-xs text-cyan" dir="ltr">{a.aql_code} · {a.order_reference || ""}</p>{a.email && <p className="font-en text-xs text-muted-foreground" dir="ltr">{a.email}</p>}</div>
            <span className={`rounded-md px-2 py-0.5 text-xs ${a.status === "approved" ? "bg-emerald-400/10 text-emerald-400" : a.status === "pending_review" ? "bg-cyan/10 text-cyan" : a.status === "rejected" ? "bg-rose-400/10 text-rose-300" : "bg-amber-400/10 text-gold"}`}>{({ pending_payment: "بانتظار الدفع", pending_review: "بانتظار المراجعة", approved: "منشور", stopped: "موقوف", rejected: "مرفوض" })[a.status] || a.status}</span>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">{a.image_count || 0} صور · {a.products?.length || 0} منتجات</span>
              <Button onClick={() => reviewAd(a.id)} disabled={reviewing === a.id} variant="outline" className="h-8 gap-1 rounded-lg border-cyan/30 bg-transparent px-3 text-xs text-cyan hover:bg-cyan/10">{reviewing === a.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} مراجعة ذكية</Button>
            </div>
            {a.ai_review && <div className="w-full rounded-md bg-white/[0.03] p-2 text-xs text-muted-foreground">الجودة {a.ai_review.quality || "—"}/5 · المخاطر: {a.ai_review.risk || "—"}{a.ai_review.missing?.length > 0 && <span> · نواقص: {a.ai_review.missing.join("، ")}</span>}{a.ai_review.recommendations?.length > 0 && <span> · {a.ai_review.recommendations.join("، ")}</span>}</div>}
            <div className="flex gap-2">
              {a.status === "pending_review" && a.payment_status === "paid" && <Button onClick={() => api.adminAdStatus(a.id, "approved", pin).then(() => { toast.success("تم نشر الصفحة وإشعار المعلن"); load(); }).catch((e) => toast.error(e?.response?.data?.detail || "تعذر النشر"))} className="btn-cyan h-8 gap-1 rounded-lg px-3 text-xs font-semibold hover:bg-transparent"><Check className="h-3 w-3" /> موافقة ونشر</Button>}
              {a.status === "pending_review" && <Button onClick={() => api.adminAdStatus(a.id, "rejected", pin).then(() => load())} variant="outline" className="h-8 gap-1 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><X className="h-3 w-3" /> رفض</Button>}
              {a.status === "approved" && <Button onClick={() => api.adminAdStatus(a.id, "stopped", pin).then(() => load())} variant="outline" className="h-8 gap-1 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Power className="h-3 w-3" /> إيقاف</Button>}
              {a.status === "stopped" && a.payment_status === "paid" && <Button onClick={() => api.adminAdStatus(a.id, "approved", pin).then(() => load())} variant="outline" className="h-8 gap-1 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Power className="h-3 w-3" /> إعادة نشر</Button>}
              <Button onClick={() => api.adminDeleteAd(a.id, pin).then(() => load())} variant="outline" className="h-8 gap-1 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد إعلانات.</p>}
      </div>
    </Section>
  );
}

function CodesTab({ pin }) {
  const ROLES = [
    { id: "advertiser", label: "معلن" }, { id: "trader", label: "تاجر" },
    { id: "coupon", label: "كوبون" }, { id: "work", label: "عمل" },
    { id: "section", label: "قسم" }, { id: "group", label: "مجموعة" },
  ];
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const [gen, setGen] = useState({ role: "advertiser", label: "", count: 1, city: "", section: "", trade: "", days: 0 });
  const [cities, setCities] = useState([]);
  const [last, setLast] = useState([]);
  const load = () => api.adminCodes(pin, filter).then(setList);
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.categories().then((d) => setCities(d.cities || [])); }, []);
  const generate = async () => {
    const r = await api.adminGenCodes({ ...gen, count: parseInt(gen.count || 1), days: parseInt(gen.days || 0) }, pin);
    setLast(r.generated); toast.success(`تم توليد ${r.generated.length} رمز`); load();
  };
  const roleLabel = (id) => ROLES.find((r) => r.id === id)?.label || id;
  return (
    <Section title="قاعدة الرموز — عامة وخاصة لكل معلن (لا تنتهي)">
      <p className="mb-4 text-sm text-muted-foreground">توليد لا محدود لرموز المعلنين/التجار. لكل رمز: <b className="text-cyan">رمز عام</b> يُشارك، و<b className="text-gold">رمز سري خاص</b> يدخل به المعلن لتعديل بياناته وإكمال الشراء. سجل العمليات يبني تقييم المعلن.</p>
      <div className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:grid-cols-3">
        <div><Label className="text-xs text-white">النوع</Label>
          <select value={gen.role} onChange={(e) => setGen({ ...gen, role: e.target.value })} className={"mt-1 h-10 w-full rounded-lg px-3 " + inp} dir="rtl">
            {ROLES.map((r) => <option key={r.id} value={r.id} className="bg-[#0b1116]">{r.label}</option>)}
          </select></div>
        <div><Label className="text-xs text-white">المدينة</Label>
          <select value={gen.city} onChange={(e) => setGen({ ...gen, city: e.target.value })} className={"mt-1 h-10 w-full rounded-lg px-3 " + inp} dir="rtl">
            <option value="" className="bg-[#0b1116]">— الكل —</option>
            {cities.map((c) => <option key={c.id} value={c.name} className="bg-[#0b1116]">{c.name}</option>)}
          </select></div>
        <div><Label className="text-xs text-white">التجارة/القسم</Label><Input value={gen.section} onChange={(e) => setGen({ ...gen, section: e.target.value })} className={"mt-1 " + inp} placeholder="سيارات / تمور…" /></div>
        <div><Label className="text-xs text-white">الوصف/الاسم</Label><Input value={gen.label} onChange={(e) => setGen({ ...gen, label: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">العدد</Label><Input type="number" value={gen.count} onChange={(e) => setGen({ ...gen, count: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">المدة (يوم، 0=دائم)</Label><Input type="number" value={gen.days} onChange={(e) => setGen({ ...gen, days: e.target.value })} className={"mt-1 " + inp} /></div>
        <Button data-testid="gen-codes-btn" onClick={generate} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent sm:col-span-3"><Plus className="h-4 w-4" /> توليد رموز</Button>
      </div>
      {last.length > 0 && (
        <div className="mb-4 rounded-xl border border-cyan/20 bg-cyan/5 p-4">
          <p className="mb-2 text-xs text-muted-foreground">آخر ما تم توليده (انسخه الآن — الرمز السري يظهر مرة واحدة):</p>
          <div className="space-y-1">
            {last.map((c) => (
              <div key={c.code} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="font-en text-cyan" dir="ltr">{c.code}</span>
                <span className="font-en text-gold" dir="ltr">سري: {c.secret}</span>
                <button onClick={() => { navigator.clipboard?.writeText(`${c.code} | سري: ${c.secret}`); toast.success("نُسخ"); }} className="text-muted-foreground hover:text-white">نسخ</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mb-3 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-xs ${filter === "" ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>الكل</button>
        {ROLES.map((r) => <button key={r.id} onClick={() => setFilter(r.id)} className={`rounded-full px-3 py-1 text-xs ${filter === r.id ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>{r.label}</button>)}
      </div>
      <div className="space-y-2">
        {list.map((c) => {
          const rating = Math.min(5, Math.round((1 + (c.uses || 0) * 0.2) * 10) / 10);
          return (
            <Row key={c.id}>
              <div><p className="font-en text-sm text-cyan" dir="ltr">{c.code}</p>
                <p className="text-xs text-muted-foreground">{roleLabel(c.role)}{c.label ? ` · ${c.label}` : ""}{c.city ? ` · ${c.city}` : ""}{c.section ? ` · ${c.section}` : ""}</p></div>
              <span className="text-xs text-gold">★ {rating} <span className="text-muted-foreground">({c.uses || 0} عملية)</span></span>
              <span className={`text-xs ${c.status === "active" ? "text-emerald-400" : "text-rose-400"}`}>{c.status === "active" ? "فعّال" : "معطّل"}</span>
              <div className="flex gap-2">
                <Button onClick={() => api.adminToggleCode(c.code, pin).then(load)} variant="outline" className="h-8 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Power className="h-3 w-3" /></Button>
                <Button onClick={() => api.adminRenewCode(c.code, 0, pin).then(load)} variant="outline" className="h-8 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5">تجديد</Button>
                <Button onClick={() => api.adminDeleteCode(c.code, pin).then(load)} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </Row>
          );
        })}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد رموز بعد.</p>}
      </div>
    </Section>
  );
}

function Keys({ pin }) {
  const [list, setList] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [pkg, setPkg] = useState("");
  const [count, setCount] = useState(1);
  const [days, setDays] = useState(0);
  const [locked, setLocked] = useState(true);
  const load = () => api.adminKeys(pin).then(setList);
  useEffect(() => {
    load();
    api.packages().then((d) => { setPkgs(d.packages); setPkg(d.packages?.[0]?.id || ""); });
    api.adminGetSettings(pin).then((s) => setLocked(s.services_locked !== false));
  }, []);
  const gen = async (master = false) => {
    try {
      const r = await api.adminGenKey({ package_id: pkg, email: "", count, days, master }, pin);
      toast.success(`تم توليد ${r.codes.length} مفتاح${master ? " رئيسي" : ""}`); load();
    } catch (e) { toast.error("خطأ"); }
  };
  const toggleLock = async () => {
    const v = !locked; setLocked(v);
    await api.adminPutSettings({ services_locked: v }, pin);
    toast.success(v ? "تم قفل الخدمات (تتطلب مفتاح)" : "تم فتح الخدمات للجميع");
  };
  return (
    <Section title="مفاتيح التفعيل (IQL) — توليد لا محدود">
      <div className="mb-4 flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div><p className="font-semibold text-white">قفل الخدمات الذكية</p><p className="text-xs text-muted-foreground">عند التفعيل تتطلب الخدمات مفتاحًا. عند الإيقاف تكون مفتوحة للجميع.</p></div>
        <button onClick={toggleLock} className={`relative h-7 w-14 rounded-full transition-colors ${locked ? "bg-cyan/60" : "bg-white/15"}`}>
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${locked ? "right-1" : "right-8"}`} />
        </button>
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div><Label className="text-xs text-white">الباقة</Label>
          <select value={pkg} onChange={(e) => setPkg(e.target.value)} className={"mt-1 h-10 rounded-lg px-3 " + inp}>
            {pkgs.map((p) => <option key={p.id} value={p.id} className="bg-[#0b1116]">{p.name}</option>)}
          </select></div>
        <div><Label className="text-xs text-white">العدد</Label><Input type="number" value={count} onChange={(e) => setCount(parseInt(e.target.value || 1))} className={"mt-1 w-20 " + inp} /></div>
        <div><Label className="text-xs text-white">المدة (يوم، 0=دائم)</Label><Input type="number" value={days} onChange={(e) => setDays(parseInt(e.target.value || 0))} className={"mt-1 w-28 " + inp} /></div>
        <Button onClick={() => gen(false)} className="btn-gold gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> توليد مفاتيح</Button>
        <Button onClick={() => gen(true)} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><KeyRound className="h-4 w-4" /> مفتاح رئيسي (كل الخدمات)</Button>
      </div>
      <div className="space-y-2">
        {list.map((k) => {
          const expired = k.expires_at && new Date(k.expires_at) < new Date();
          return (
            <Row key={k.id}>
              <span className="font-en text-sm text-cyan" dir="ltr">{k.code}</span>
              <span className="text-xs text-muted-foreground">{k.master ? "رئيسي" : k.package_id}{k.expires_at ? ` · ينتهي ${new Date(k.expires_at).toLocaleDateString("ar-SA")}` : " · دائم"}</span>
              <span className={`text-xs ${expired ? "text-rose-400" : k.status === "active" ? "text-emerald-400" : "text-rose-400"}`}>{expired ? "منتهٍ" : k.status === "active" ? "نشط" : "ملغى"}</span>
              {k.status === "active"
                ? <Button onClick={() => api.adminRevokeKey(k.code, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10">قفل</Button>
                : <Button onClick={() => api.adminActivateKey(k.code, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-emerald-400/30 bg-transparent px-3 text-xs text-emerald-300 hover:bg-emerald-400/10">فتح</Button>}
            </Row>
          );
        })}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد مفاتيح.</p>}
      </div>
    </Section>
  );
}

function Packages({ pin }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", price: 0, tag: "", services: "", features: "" });
  const load = () => api.packages().then((d) => setList(d.packages));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name) { toast.error("أدخل الاسم"); return; }
    await api.adminSavePackage({
      id: f.id, name: f.name, price: parseFloat(f.price) || 0, tag: f.tag, highlight: !!f.highlight,
      services: f.services.split(",").map((s) => s.trim()).filter(Boolean),
      features: f.features.split("\n").map((s) => s.trim()).filter(Boolean),
    }, pin);
    toast.success("تم الحفظ"); setF({ name: "", price: 0, tag: "", services: "", features: "" }); load();
  };
  return (
    <Section title="الباقات">
      <div className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:grid-cols-2">
        <div><Label className="text-xs text-white">الاسم</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">السعر</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">الوسم</Label><Input value={f.tag} onChange={(e) => setF({ ...f, tag: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">الخدمات (مفصولة بفواصل)</Label><Input value={f.services} onChange={(e) => setF({ ...f, services: e.target.value })} placeholder="notebook, contracts" className={"mt-1 " + inp} dir="ltr" /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-white">المزايا (سطر لكل ميزة)</Label><Textarea value={f.features} onChange={(e) => setF({ ...f, features: e.target.value })} rows={3} className={"mt-1 " + inp} /></div>
        <Button onClick={save} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> {f.id ? "تحديث" : "إضافة"}</Button>
      </div>
      <div className="space-y-2">
        {list.map((p) => (
          <Row key={p.id}>
            <div><p className="text-sm text-white">{p.name} · {p.price} ريال</p><p className="text-xs text-muted-foreground">{(p.services || []).join("، ")}</p></div>
            <div className="flex gap-2">
              <Button onClick={() => setF({ id: p.id, name: p.name, price: p.price, tag: p.tag, highlight: p.highlight, services: (p.services || []).join(", "), features: (p.features || []).join("\n") })} variant="outline" className="h-8 gap-1 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Edit3 className="h-3 w-3" /></Button>
              <Button onClick={() => api.adminDeletePackage(p.id, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
      </div>
    </Section>
  );
}

function ServicesTab({ pin }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", kind: "notebook", icon: "Sparkles", desc: "", order: 99, active: true });
  const load = () => api.adminServices(pin).then(setList);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name) { toast.error("أدخل الاسم"); return; }
    await api.adminSaveService(f, pin); toast.success("تم الحفظ");
    setF({ name: "", kind: "notebook", icon: "Sparkles", desc: "", order: 99, active: true }); load();
  };
  return (
    <Section title="الخدمات الذكية (بناء / نشر / إيقاف)">
      <div className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:grid-cols-2">
        <div><Label className="text-xs text-white">الاسم</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">النوع (kind)</Label>
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={"mt-1 h-10 w-full rounded-lg px-3 " + inp} dir="ltr">
            {["notebook", "notebook-lm", "contracts", "page", "builder", "publisher"].map((k) => <option key={k} value={k} className="bg-[#0b1116]">{k}</option>)}
          </select></div>
        <div><Label className="text-xs text-white">الأيقونة</Label><Input value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="Sparkles / BookOpen / Code2" /></div>
        <div><Label className="text-xs text-white">الترتيب</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value || 99) })} className={"mt-1 " + inp} /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-white">الوصف</Label><Textarea value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} rows={2} className={"mt-1 " + inp} /></div>
        <Button onClick={save} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> {f.id ? "تحديث" : "إضافة خدمة"}</Button>
      </div>
      <div className="space-y-2">
        {list.map((s) => (
          <Row key={s.id}>
            <div><p className="text-sm text-white">{s.name} <span className="font-en text-xs text-muted-foreground" dir="ltr">({s.kind})</span></p></div>
            <span className={`text-xs ${s.active ? "text-emerald-400" : "text-rose-400"}`}>{s.active ? "منشورة" : "متوقفة"}</span>
            <div className="flex gap-2">
              <Button onClick={() => api.adminSaveService({ ...s, active: !s.active }, pin).then(() => load())} variant="outline" className="h-8 gap-1 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Power className="h-3 w-3" /> {s.active ? "إيقاف" : "نشر"}</Button>
              <Button onClick={() => setF(s)} variant="outline" className="h-8 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Edit3 className="h-3 w-3" /></Button>
              <Button onClick={() => api.adminDeleteService(s.id, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
      </div>
    </Section>
  );
}

function Pages({ pin }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ slug: "", title: "", type: "section", content: "", published: true, order: 99 });
  const load = () => api.adminPages(pin).then(setList);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.slug || !f.title) { toast.error("أدخل المعرّف والعنوان"); return; }
    await api.adminSavePage(f, pin); toast.success("تم الحفظ");
    setF({ slug: "", title: "", type: "section", content: "", published: true, order: 99 }); load();
  };
  const genAI = async () => {
    if (!f.title) { toast.error("أدخل العنوان أولًا"); return; }
    toast.info("جارٍ توليد المحتوى بالذكاء…");
    try { const r = await api.aiPage({ prompt: `صفحة/قسم بعنوان: ${f.title}. ${f.content}` }); setF({ ...f, content: r.html }); toast.success("تم التوليد"); }
    catch (e) { toast.error("خطأ في التوليد"); }
  };
  return (
    <Section title="منشئ الأقسام والصفحات">
      <p className="mb-4 text-sm text-muted-foreground">أنشئ أقسامًا أو صفحات جديدة داخل الموقع (مثل قسم «المكلأ»)، مع إمكانية توليد المحتوى بالذكاء الاصطناعي.</p>
      <div className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:grid-cols-2">
        <div><Label className="text-xs text-white">العنوان</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={"mt-1 " + inp} /></div>
        <div><Label className="text-xs text-white">المعرّف (slug)</Label><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="mokla" /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-white">المحتوى (HTML أو نص)</Label><Textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} rows={5} className={"mt-1 " + inp} /></div>
        <div className="flex gap-2">
          <Button onClick={save} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> {f.id ? "تحديث" : "إنشاء"}</Button>
          <Button onClick={genAI} className="btn-gold gap-2 rounded-lg font-semibold hover:bg-transparent"><Bot className="h-4 w-4" /> توليد بالذكاء</Button>
        </div>
      </div>
      <div className="space-y-2">
        {list.map((p) => (
          <Row key={p.id}>
            <div><p className="text-sm text-white">{p.title}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">/p/{p.slug}</p></div>
            <span className={`text-xs ${p.published ? "text-emerald-400" : "text-rose-400"}`}>{p.published ? "منشورة" : "مسودة"}</span>
            <div className="flex gap-2">
              <Button onClick={() => setF(p)} variant="outline" className="h-8 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Edit3 className="h-3 w-3" /></Button>
              <Button onClick={() => api.adminDeletePage(p.id, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
      </div>
    </Section>
  );
}

function Domains({ pin }) {
  const [list, setList] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [f, setF] = useState({ domain: "", target: "home" });
  const [baseDomain, setBaseDomain] = useState("aql4.com");
  const [dns, setDns] = useState({ zone_id: "", target_host: "", api_token: "", has_api_token: false });
  const [provisionResult, setProvisionResult] = useState(null);
  const [sectionsMap, setSectionsMap] = useState([]);
  const load = () => { api.adminDomains(pin).then(setList); api.adminDomainRequests(pin).then(setReqs); };
  useEffect(() => {
    load();
    api.adminDomainDNS(pin).then((config) => {
      if (config.root_domain) setBaseDomain(config.root_domain);
      setDns({ zone_id: config.zone_id || "", target_host: config.target_host || "", api_token: "", has_api_token: config.has_api_token });
    });
  }, [pin]);
  const save = async () => {
    if (!f.domain) { toast.error("أدخل النطاق"); return; }
    await api.adminSaveDomain(f, pin); toast.success("تمت الإضافة"); setF({ domain: "", target: "home" }); load();
  };
  const registerSections = async () => {
    const r = await api.adminRegisterSections(baseDomain, pin);
    setSectionsMap(r.sections || []); toast.success(`تم تسجيل ${r.sections.length} قسم/خدمة`); load();
  };
  const saveDns = async () => {
    const result = await api.adminSaveDomainDNS({ root_domain: baseDomain, ...dns }, pin);
    setDns({ ...dns, api_token: "", has_api_token: result.has_api_token });
    toast.success("حُفظ إعداد DNS بأمان");
  };
  const provisionDns = async () => {
    try {
      const result = await api.adminProvisionDomainDNS(pin);
      setProvisionResult(result);
      toast.success(`تم ربط ${result.provisioned.length} نطاق`);
      if (result.failures.length) toast.error(`تعذر ربط ${result.failures.length} نطاق`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "فشل إعداد DNS"); }
  };
  const approve = async (id) => { await api.adminApproveDomainReq(id, pin).then((r) => toast.success(`تمت الموافقة → ${r.target}`)); load(); };
  const reject = async (id) => { await api.adminRejectDomainReq(id, pin); toast.success("تم الرفض"); load(); };
  const pending = reqs.filter((r) => r.status === "pending");
  return (
    <Section title="النطاقات (Domains) وطلبات العملاء">
      <p className="mb-4 text-sm text-muted-foreground">اربط النطاق الذي تملكه بخادم المنصة، ثم وجّه كل نطاق فرعي إلى خدمة أو صفحة منشورة. شراء النطاق وتجديده يظلان لدى المسجل الذي اشتريته منه.</p>

      <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-1 font-semibold text-white">DNS للنطاق والخدمات الفرعية</h3>
        <p className="mb-3 text-xs text-muted-foreground">يتطلب Cloudflare API Token بصلاحية DNS Edit وZone ID واسم وجهة الاستضافة. يتم إنشاء/تحديث CNAME على الجذر والنطاقات المسجلة؛ لا تنشأ صناديق بريد من هذا الإعداد.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs text-white">النطاق الأساسي</Label><Input value={baseDomain} onChange={(e) => setBaseDomain(e.target.value)} className={"mt-1 " + inp} dir="ltr" placeholder="example.com" /></div>
          <div><Label className="text-xs text-white">Cloudflare Zone ID</Label><Input value={dns.zone_id} onChange={(e) => setDns({ ...dns, zone_id: e.target.value })} className={"mt-1 " + inp} dir="ltr" /></div>
          <div><Label className="text-xs text-white">CNAME Target Host</Label><Input value={dns.target_host} onChange={(e) => setDns({ ...dns, target_host: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="deployment.example-host.com" /></div>
          <div><Label className="text-xs text-white">Cloudflare API Token {dns.has_api_token && <span className="text-emerald-400">(محفوظ)</span>}</Label><Input value={dns.api_token} onChange={(e) => setDns({ ...dns, api_token: e.target.value })} type="password" className={"mt-1 " + inp} dir="ltr" placeholder={dns.has_api_token ? "اتركه فارغًا للإبقاء على المحفوظ" : "Token DNS Edit"} /></div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button onClick={saveDns} className="btn-cyan rounded-lg px-4 font-semibold hover:bg-transparent">حفظ إعداد DNS</Button>
          <Button onClick={provisionDns} disabled={!dns.has_api_token && !dns.api_token} variant="outline" className="rounded-lg border-white/15 bg-transparent px-4 text-white">إنشاء سجلات النطاقات</Button>
          <span className="text-xs text-amber-300">تأكد أن النطاق مضاف إلى Zone في حسابك وأن Target يستقبل الموقع.</span>
        </div>
        {provisionResult && <p className="mt-2 text-xs text-cyan">تم ربط {provisionResult.provisioned.length} نطاق، تعذر {provisionResult.failures.length}. راجع حالة كل نطاق بالأسفل.</p>}
      </div>

      <div className="mb-5 rounded-xl border border-cyan/25 bg-cyan/5 p-4">
        <h3 className="mb-2 flex items-center gap-2 font-semibold text-cyan"><Layers className="h-4 w-4" /> صفحة + رابط ثانوي + مفتاح ذكاء لكل قسم وخدمة</h3>
        <p className="mb-3 text-xs text-muted-foreground">يولّد لكل تصنيف مفتاح ذكاء خاصًّا، ويسجّل رابطًا دائمًا + نطاقًا فرعيًا مقترحًا لكل قسم وخدمة على نطاقك.</p>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs text-white">نطاقك الأساسي</Label><Input value={baseDomain} onChange={(e) => setBaseDomain(e.target.value)} className={"mt-1 w-56 " + inp} dir="ltr" /></div>
          <Button data-testid="register-sections-btn" onClick={registerSections} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent"><Zap className="h-4 w-4" /> توليد روابط ومفاتيح الأقسام</Button>
        </div>
        {sectionsMap.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {sectionsMap.map((s) => (
              <div key={s.subdomain} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-xs">
                <span className="text-white">{s.label}</span>
                <a href={s.url} target="_blank" rel="noreferrer" className="font-en text-cyan hover:underline" dir="ltr">{s.url}</a>
                <span className="font-en text-muted-foreground" dir="ltr">{s.subdomain}</span>
                {s.short_subdomain && <span className="font-en text-muted-foreground" dir="ltr">اسم مقروء: {s.short_subdomain}</span>}
                {s.aql_code && <span className="font-en text-gold" dir="ltr">{s.aql_code}</span>}
                {s.ai_key && <span className="font-en text-gold" dir="ltr">{s.ai_key}</span>}
              </div>
            ))}
            <p className="pt-1 text-[11px] text-gold">لتفعيل النطاقات الفرعية (cars.{baseDomain}): أضف سجل CNAME لكل نطاق فرعي عند مزوّد نطاقك يشير إلى المنصة (يتطلب دعم Wildcard على مستوى المنصة). الروابط أعلاه تعمل الآن مباشرةً.</p>
          </div>
        )}
      </div>

      <div className="mb-5 rounded-xl border border-gold/25 bg-amber-400/5 p-4">
        <h3 className="mb-3 flex items-center gap-2 font-semibold text-gold"><Globe className="h-4 w-4" /> طلبات نطاقات العملاء ({pending.length} معلّق)</h3>
        <div className="space-y-2">
          {reqs.map((r) => (
            <Row key={r.id}>
              <div><p className="font-en text-sm text-white" dir="ltr">{r.domain}</p><p className="text-xs text-muted-foreground">{r.requester_email || "عميل"}{r.project_id ? " · مشروع مرتبط" : ""}</p></div>
              <span className={`rounded-md px-2 py-0.5 text-xs ${r.status === "approved" ? "bg-emerald-400/10 text-emerald-400" : r.status === "rejected" ? "bg-rose-400/10 text-rose-300" : "bg-amber-400/10 text-gold"}`}>{r.status === "approved" ? "موافَق" : r.status === "rejected" ? "مرفوض" : "معلّق"}</span>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <Button data-testid={`domreq-approve-${r.id}`} onClick={() => approve(r.id)} className="btn-cyan h-8 gap-1 rounded-lg px-3 text-xs font-semibold hover:bg-transparent"><Check className="h-3 w-3" /> موافقة وربط</Button>
                  <Button onClick={() => reject(r.id)} variant="outline" className="h-8 gap-1 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><X className="h-3 w-3" /> رفض</Button>
                </div>
              )}
            </Row>
          ))}
          {!reqs.length && <p className="py-3 text-center text-xs text-muted-foreground">لا توجد طلبات.</p>}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex-1"><Label className="text-xs text-white">النطاق</Label><Input value={f.domain} onChange={(e) => setF({ ...f, domain: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="example.com" /></div>
        <div><Label className="text-xs text-white">الوجهة</Label><Input value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} className={"mt-1 w-40 " + inp} dir="ltr" placeholder="home أو /site/id" /></div>
        <Button onClick={save} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> ربط</Button>
      </div>
      <div className="space-y-2">
        {list.map((d) => (
          <Row key={d.id}>
            <div><p className="font-en text-sm text-white" dir="ltr">{d.domain}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">→ {d.target}</p>{d.verify_token && <p className="font-en text-[10px] text-muted-foreground" dir="ltr">TXT verification: {d.verify_token}</p>}</div>
            <span className={`text-xs ${d.status === "active" ? "text-emerald-400" : "text-gold"}`}>{d.status === "active" ? "DNS فعال" : d.status === "needs_dns" ? "بحاجة إلى DNS" : d.status}</span>
            {d.status === "active" && <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" className="text-xs text-cyan hover:underline">فتح النطاق</a>}
            <div className="flex gap-2">
              <Button onClick={() => api.adminDeleteDomain(d.id, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
      </div>
    </Section>
  );
}

function WalletTab({ pin }) {
  const [f, setF] = useState({ email: "", amount: 0 });
  const adjust = async () => {
    if (!f.email) { toast.error("أدخل البريد"); return; }
    const r = await api.adminWalletAdjust({ email: f.email, amount: parseFloat(f.amount) || 0 }, pin);
    toast.success(`الرصيد الجديد: ${r.balance} ريال`);
  };
  return (
    <Section title="تعديل المحافظ">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="flex-1"><Label className="text-xs text-white">بريد المستخدم</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={"mt-1 " + inp} dir="ltr" /></div>
        <div><Label className="text-xs text-white">المبلغ (+/-)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={"mt-1 w-32 " + inp} /></div>
        <Button onClick={adjust} className="btn-gold rounded-lg px-5 font-semibold hover:bg-transparent">تطبيق</Button>
      </div>
    </Section>
  );
}

function CategoriesTab({ pin }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", image: "", desc: "", sections: "", order: 99, active: true });
  const [busy, setBusy] = useState(false);
  const load = () => api.adminCategories(pin).then(setList);
  useEffect(() => { load(); }, []);
  const reset = () => setF({ name: "", image: "", desc: "", sections: "", order: 99, active: true });
  const save = async () => {
    if (!f.name) { toast.error("أدخل اسم التصنيف"); return; }
    await api.adminSaveCategory({
      id: f.id, name: f.name, image: f.image, desc: f.desc, order: parseInt(f.order || 99), active: f.active !== false,
      sections: (typeof f.sections === "string" ? f.sections : (f.sections || []).join("، ")).split(/[،,]/).map((s) => s.trim()).filter(Boolean),
    }, pin);
    toast.success("تم الحفظ"); reset(); load();
  };
  const genAI = async () => {
    if (!f.name) { toast.error("أدخل الاسم أولًا"); return; }
    setBusy(true); toast.info("جارٍ توليد الوصف والأقسام بالذكاء…");
    try {
      const r = await api.adminAI({ message: `اقترح وصفًا تسويقيًا قصيرًا (سطر واحد) وقائمة 8 أقسام فرعية مفصولة بفواصل لتصنيف باسم «${f.name}» في منصة خدمات سعودية. أعد بصيغة: الوصف: ... | الأقسام: ...`, service: "admin", session_id: "cat-gen" }, pin);
      const txt = r.content || "";
      const descM = txt.match(/الوصف\s*[:：]\s*(.+)/); const secM = txt.match(/الأقسام\s*[:：]\s*(.+)/);
      setF((p) => ({ ...p, desc: descM ? descM[1].split("|")[0].trim() : p.desc, sections: secM ? secM[1].trim() : p.sections }));
      toast.success("تم التوليد");
    } catch (e) { toast.error("تعذّر التوليد"); }
    setBusy(false);
  };
  return (
    <Section title="تصنيفات الموقع — إدارة كاملة">
      <p className="mb-4 text-sm text-muted-foreground">أضف أو عدّل تصنيفات الواجهة الرئيسية (السيارات، المزارع، المنزل…) وأقسامها الفرعية. تظهر مباشرة في الصفحة الرئيسية وصفحات التصنيف.</p>
      <div className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4 sm:grid-cols-2">
        <div><Label className="text-xs text-white">اسم التصنيف</Label><Input data-testid="category-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={"mt-1 " + inp} placeholder="السيارات" /></div>
        <div><Label className="text-xs text-white">الترتيب</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: e.target.value })} className={"mt-1 " + inp} /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-white">رابط الصورة</Label><Input value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="https://…" /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-white">الوصف</Label><Textarea value={f.desc} onChange={(e) => setF({ ...f, desc: e.target.value })} rows={2} className={"mt-1 " + inp} /></div>
        <div className="sm:col-span-2"><Label className="text-xs text-white">الأقسام الفرعية (مفصولة بفواصل)</Label><Textarea value={typeof f.sections === "string" ? f.sections : (f.sections || []).join("، ")} onChange={(e) => setF({ ...f, sections: e.target.value })} rows={2} className={"mt-1 " + inp} placeholder="بيع، تأجير، صيانة…" /></div>
        <div className="flex gap-2">
          <Button data-testid="category-save-btn" onClick={save} className="btn-cyan gap-2 rounded-lg font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> {f.id ? "تحديث" : "إضافة تصنيف"}</Button>
          <Button onClick={genAI} disabled={busy} className="btn-gold gap-2 rounded-lg font-semibold hover:bg-transparent">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />} توليد بالذكاء</Button>
          {f.id && <Button onClick={reset} variant="outline" className="rounded-lg border-white/15 bg-transparent text-white hover:bg-white/5">إلغاء</Button>}
        </div>
      </div>
      <div className="space-y-2">
        {list.map((c) => (
          <Row key={c.id}>
            <div className="flex items-center gap-3">
              {c.image && <img src={c.image} alt={c.name} className="h-10 w-10 rounded-lg object-cover" />}
              <div><p className="text-sm text-white">{c.name}</p><p className="text-xs text-muted-foreground">{(c.sections || []).length} قسم فرعي</p></div>
            </div>
            <span className={`text-xs ${c.active !== false ? "text-emerald-400" : "text-rose-400"}`}>{c.active !== false ? "ظاهر" : "مخفي"}</span>
            <div className="flex gap-2">
              <Button onClick={() => api.adminSaveCategory({ ...c, active: !(c.active !== false) }, pin).then(load)} variant="outline" className="h-8 gap-1 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Power className="h-3 w-3" /></Button>
              <Button onClick={() => setF({ ...c, sections: (c.sections || []).join("، ") })} variant="outline" className="h-8 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Edit3 className="h-3 w-3" /></Button>
              <Button onClick={() => api.adminDeleteCategory(c.id, pin).then(load)} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد تصنيفات.</p>}
      </div>
    </Section>
  );
}

function Integrations({ pin }) {
  const [defs, setDefs] = useState([]);
  const [s, setS] = useState(null);
  const [testing, setTesting] = useState("");
  const load = () => { api.adminIntegrations(pin).then(setDefs); api.adminGetSettings(pin).then(setS); };
  useEffect(() => { load(); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-cyan" />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const saveOne = async (it) => {
    const upd = {};
    it.fields.forEach((fld) => {
      if (!fld.secret || s[fld.key]) upd[fld.key] = s[fld.key] || "";
    });
    await api.adminPutSettings(upd, pin); toast.success(`تم حفظ ${it.name}`); load();
  };
  const test = async (it) => {
    setTesting(it.id);
    try { const r = await api.adminTestIntegration(it.id, pin); toast.success(r.message || "نجح الاختبار"); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل الاختبار"); }
    setTesting("");
  };
  return (
    <Section title="التكاملات والربط الحقيقي">
      <p className="mb-5 text-sm text-muted-foreground">حفظ بيانات الاتصال لا يعني أن الخدمة تعمل. اختبر كل تكامل مدعوم قبل الاعتماد عليه؛ حالات «بيانات محفوظة» و«غير مهيأ» لا تمثل اختبارًا حيًا.</p>
      <div className="grid gap-4">
        {defs.map((it) => (
          <div key={it.id} data-testid={`integration-card-${it.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-white">{it.name}</h3>
                <span className="rounded-full bg-white/5 px-2 py-0.5 text-[11px] text-muted-foreground">{it.category}</span>
              </div>
              <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${it.configured ? "bg-cyan/10 text-cyan" : "bg-amber-400/10 text-gold"}`}>
                {it.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                {it.configured ? "بيانات محفوظة" : "غير مهيأ"}
              </span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {it.fields.map((fld) => (
                <div key={fld.key}>
                  <Label className="text-xs text-white">{fld.label}</Label>
                  <Input data-testid={`int-${fld.key}`} value={s[fld.key] || ""} onChange={(e) => set(fld.key, e.target.value)}
                    type={fld.secret ? "password" : "text"} placeholder={fld.secret && it.values?.[fld.key] ? "مفتاح محفوظ — اتركه فارغًا أو أدخل بديلًا" : (it.values?.[fld.key] || "")}
                    className={"mt-1 " + inp} dir="ltr" />
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">{it.help}</p>
            <div className="mt-3 flex gap-2">
              <Button data-testid={`int-save-${it.id}`} onClick={() => saveOne(it)} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Check className="h-4 w-4" /> حفظ</Button>
              {it.testable && <Button data-testid={`int-test-${it.id}`} onClick={() => test(it)} disabled={testing === it.id} variant="outline" className="gap-2 rounded-lg border-cyan/30 bg-transparent px-5 text-cyan hover:bg-cyan/10">{testing === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} اختبار الاتصال</Button>}
            </div>
          </div>
        ))}
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
          <h3 className="mb-3 font-semibold text-white">بيانات التحويل البنكي</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <div><Label className="text-xs text-white">اسم البنك</Label><Input value={s.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} className={"mt-1 " + inp} /></div>
            <div><Label className="text-xs text-white">المستفيد</Label><Input value={s.bank_beneficiary || ""} onChange={(e) => set("bank_beneficiary", e.target.value)} className={"mt-1 " + inp} /></div>
            <div><Label className="text-xs text-white">IBAN</Label><Input value={s.bank_iban || ""} onChange={(e) => set("bank_iban", e.target.value)} className={"mt-1 " + inp} dir="ltr" /></div>
          </div>
          <Button onClick={async () => { await api.adminPutSettings({ bank_name: s.bank_name, bank_beneficiary: s.bank_beneficiary, bank_iban: s.bank_iban }, pin); toast.success("تم الحفظ"); }} className="btn-cyan mt-3 gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Check className="h-4 w-4" /> حفظ</Button>
        </div>
      </div>
    </Section>
  );
}

function SitesTab({ pin }) {
  const [list, setList] = useState([]);
  const load = () => api.adminSites(pin).then(setList);
  useEffect(() => { load(); }, []);
  return (
    <Section title="المواقع المنشورة">
      <div className="space-y-2">
        {list.map((s) => (
          <Row key={s.id}>
            <div><p className="text-sm text-white">{s.title}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">/site/{s.id}</p>{s.aql_code && <p className="mt-1 font-en text-xs text-gold" dir="ltr">{s.aql_code} · {s.subdomain || "DNS pending"}</p>}</div>
            <span className="rounded-md bg-cyan/10 px-2 py-0.5 text-xs text-cyan">{s.kind}</span>
            <div className="flex gap-2">
              <a href={`/site/${s.id}`} target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/5">فتح</a>
              <Button onClick={() => api.adminDeleteSite(s.id, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد مواقع منشورة بعد — انشر من استوديو البناء.</p>}
      </div>
    </Section>
  );
}

function AgentsTab({ pin }) {
  const [list, setList] = useState([]);
  const load = () => api.adminAgents(pin).then(setList);
  useEffect(() => { load(); }, []);
  return (
    <Section title="الوكلاء المنشورون">
      <div className="space-y-2">
        {list.map((a) => (
          <Row key={a.id}>
            <div><p className="text-sm text-white">{a.name}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">/agent/{a.id}</p></div>
            <div className="flex gap-2">
              <a href={`/agent/${a.id}`} target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/5">فتح</a>
              <Button onClick={() => api.adminDeleteAgent(a.id, pin).then(() => load())} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
        {!list.length && <p className="py-8 text-center text-sm text-muted-foreground">لا يوجد وكلاء بعد — انشر من استوديو البناء.</p>}
      </div>
    </Section>
  );
}

function MarketingTab({ pin }) {
  const PLAT = [
    { id: "tiktok", l: "تيك توك" }, { id: "instagram", l: "إنستغرام" },
    { id: "snapchat", l: "سناب شات" }, { id: "whatsapp", l: "واتساب" },
  ];
  const [acc, setAcc] = useState({ tiktok: "", instagram: "", snapchat: "", whatsapp_acc: "", platform_link: "", publish_webhook: "", publish_webhook_token: "", marketing_rules: "", webhook_configured: false, has_webhook_token: false });
  const [camps, setCamps] = useState([]);
  const [posts, setPosts] = useState([]);
  const [c, setC] = useState({ topic: "", link: "", rules: "", publish_enabled: false, frequency_hours: 1, platforms: ["tiktok", "instagram", "snapchat", "whatsapp"], media: "none" });
  const [gen, setGen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testingBridge, setTestingBridge] = useState(false);
  const load = () => { api.adminCampaigns(pin).then(setCamps); api.adminMarketingPosts(pin).then(setPosts); };
  useEffect(() => {
    api.marketingAccounts().then((a) => setAcc((current) => ({ ...current, ...a })));
    api.adminCampaigns(pin).then(setCamps);
    api.adminMarketingPosts(pin).then(setPosts);
  }, [pin]);

  const saveAcc = async () => { await api.adminPutAccounts(acc, pin); setAcc({ ...acc, publish_webhook_token: "", has_webhook_token: acc.has_webhook_token || Boolean(acc.publish_webhook_token), webhook_configured: Boolean(acc.publish_webhook) }); toast.success("تم حفظ القواعد وموصل النشر"); };
  const testBridge = async () => {
    setTestingBridge(true);
    try { const result = await api.adminTestMarketingBridge(pin); toast.success(result.message); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذر اختبار الموصل"); }
    setTestingBridge(false);
  };
  const saveCamp = async () => { if (!c.topic) { toast.error("أدخل موضوع الحملة"); return; } await api.adminSaveCampaign(c, pin); toast.success("تم حفظ الحملة وقواعدها"); setC({ ...c, topic: "", rules: "" }); load(); };
  const generate = async () => {
    if (!c.topic) { toast.error("أدخل الموضوع"); return; }
    setBusy(true); setGen(null);
    try { const r = await api.marketingGenerate({ topic: c.topic, link: c.link || acc.platform_link, platforms: c.platforms, media: c.media, with_image: c.media === "image", rules: c.rules || acc.marketing_rules }); setGen(r); load(); toast.success("تم إنشاء مسودة؛ لم تُنشر بعد"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التوليد؛ تحقق من إعداد محرك الذكاء والوسائط"); }
    setBusy(false);
  };
  const runCampaign = async (id) => {
    setBusy(true);
    try { const result = await api.adminRunCampaign(id, pin); toast.success(`اكتملت محاولة الحملة: ${result.status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر تشغيل الحملة"); }
    setBusy(false);
  };
  const publishPost = async (id) => {
    try { const result = await api.adminPublishMarketingPost(id, pin); toast.success(`نتيجة الموصل: ${result.status}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر إرسال المنشور"); }
  };
  const toggle = (p) => setC({ ...c, platforms: c.platforms.includes(p) ? c.platforms.filter((x) => x !== p) : [...c.platforms, p] });

  return (
    <Section title="منطقة التسويق والناشر التلقائي">
      <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-1 font-semibold text-white">حسابات المنصات + رابط المنصة</h3>
        <p className="mb-3 text-xs text-muted-foreground">أدخل عنوان موصل تملكه (مثل خادم تكاملاتك) لربط واجهات النشر الرسمية. يجب أن يعيد الموصل نتيجة منفصلة لكل قناة؛ لا تُعد استجابة HTTP وحدها دليل نشر.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs text-white">تيك توك</Label><Input value={acc.tiktok} onChange={(e) => setAcc({ ...acc, tiktok: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="@tiktok" /></div>
          <div><Label className="text-xs text-white">إنستغرام</Label><Input value={acc.instagram} onChange={(e) => setAcc({ ...acc, instagram: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="@instagram" /></div>
          <div><Label className="text-xs text-white">سناب شات</Label><Input value={acc.snapchat} onChange={(e) => setAcc({ ...acc, snapchat: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="@snap" /></div>
          <div><Label className="text-xs text-white">واتساب</Label><Input value={acc.whatsapp_acc} onChange={(e) => setAcc({ ...acc, whatsapp_acc: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="+9665..." /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-white">رابط المنصة</Label><Input value={acc.platform_link} onChange={(e) => setAcc({ ...acc, platform_link: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="https://aql4.app" /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-white">رابط موصل النشر الرسمي</Label><Input value={acc.publish_webhook || ""} onChange={(e) => setAcc({ ...acc, publish_webhook: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder="https://your-automation.example/publish" /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-white">رمز موصل النشر {acc.has_webhook_token && <span className="text-emerald-400">(محفوظ)</span>}</Label><Input value={acc.publish_webhook_token || ""} onChange={(e) => setAcc({ ...acc, publish_webhook_token: e.target.value })} type="password" className={"mt-1 " + inp} dir="ltr" placeholder={acc.has_webhook_token ? "اتركه فارغًا للإبقاء على المحفوظ" : "اختياري"} /></div>
          <div className="sm:col-span-2"><Label className="text-xs text-white">قواعد الذكاء الافتراضية المحفوظة</Label><Textarea value={acc.marketing_rules || ""} onChange={(e) => setAcc({ ...acc, marketing_rules: e.target.value })} className={"mt-1 min-h-24 " + inp} placeholder="نبرة العلامة، العبارات الممنوعة، الجمهور، الدعوة المطلوبة، وسياسة التحقق من الادعاءات..." /></div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3"><Button onClick={saveAcc} className="btn-cyan rounded-lg px-5 font-semibold hover:bg-transparent">حفظ الإعدادات والقواعد</Button><Button onClick={testBridge} disabled={testingBridge || !acc.webhook_configured} variant="outline" className="rounded-lg border-white/15 bg-transparent px-4 text-white">{testingBridge ? "جارٍ الاختبار..." : "اختبار الموصل"}</Button><span className={`text-xs ${acc.webhook_configured ? "text-emerald-400" : "text-amber-300"}`}>{acc.webhook_configured ? "الموصل مضبوط" : "الموصل غير مضبوط؛ الحملات ستنشئ مسودات فقط"}</span></div>
      </div>

      <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-3 font-semibold text-white">حملة نشر تلقائي (كل ساعة)</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label className="text-xs text-white">الموضوع</Label><Input value={c.topic} onChange={(e) => setC({ ...c, topic: e.target.value })} className={"mt-1 " + inp} placeholder="عروض تمور المزرعة الأسبوعية" /></div>
          <div><Label className="text-xs text-white">الرابط</Label><Input value={c.link} onChange={(e) => setC({ ...c, link: e.target.value })} className={"mt-1 " + inp} dir="ltr" placeholder={acc.platform_link || "https://…"} /></div>
          <div><Label className="text-xs text-white">التكرار (ساعة)</Label><Input type="number" value={c.frequency_hours} onChange={(e) => setC({ ...c, frequency_hours: parseInt(e.target.value || 1) })} className={"mt-1 " + inp} /></div>
          <div><Label className="text-xs text-white">الوسائط</Label><select value={c.media} onChange={(e) => setC({ ...c, media: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-white/10 bg-[#11191e] px-3 text-sm text-white"><option value="none">نص ورابط فقط</option><option value="image">صورة بالذكاء</option><option value="video">فيديو بالذكاء (fal.ai)</option></select></div>
          <div className="flex items-center gap-2 pt-6"><input id="publish-enabled" type="checkbox" checked={c.publish_enabled} onChange={(e) => setC({ ...c, publish_enabled: e.target.checked })} className="h-4 w-4 accent-cyan" /><Label htmlFor="publish-enabled" className="text-xs text-white">أرسل تلقائيًا عبر الموصل عند كل موعد</Label></div>
          <div className="sm:col-span-2"><Label className="text-xs text-white">قواعد هذه الحملة</Label><Textarea value={c.rules} onChange={(e) => setC({ ...c, rules: e.target.value })} className={"mt-1 min-h-20 " + inp} placeholder="تضاف إلى القواعد الافتراضية وتُحفظ مع الحملة" /></div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {PLAT.map((p) => (<button key={p.id} onClick={() => toggle(p.id)} className={`rounded-full px-3.5 py-1.5 text-xs font-medium ${c.platforms.includes(p.id) ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}>{p.l}</button>))}
        </div>
        <div className="mt-3 flex gap-2">
          <Button onClick={saveCamp} className="btn-gold gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> إنشاء حملة</Button>
          <Button onClick={generate} disabled={busy} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إنشاء مسودة الآن</Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">تُجدول الحملة كل عدد الساعات المحدد وتستمر حتى إيقافها. الفيديو يستهلك رصيد fal.ai، والنشر يتطلب موصلًا وحسابات مصرحًا بها.</p>
      </div>

      {gen && (
        <div className="mb-5 rounded-xl border border-cyan/20 bg-cyan/5 p-4">
          <h3 className="mb-2 font-semibold text-white">المحتوى المولّد</h3>
          {gen.image_base64 && <img src={`data:image/png;base64,${gen.image_base64}`} alt="post" className="mb-3 max-h-64 rounded-lg border border-white/10" />}
          {gen.video_base64 && <video src={`data:video/mp4;base64,${gen.video_base64}`} controls className="mb-3 max-h-72 rounded-lg border border-white/10" />}
          <div className="max-h-64 overflow-y-auto whitespace-pre-wrap text-sm text-cyan-50">{gen.content}</div>
          <p className="mt-2 text-xs text-amber-300">مسودة محفوظة: {gen.status}. النشر غير مؤكد حتى يؤكده الموصل لكل منصة.</p>
        </div>
      )}

      <h3 className="mb-2 font-semibold text-white">الحملات النشطة</h3>
      <div className="mb-5 space-y-2">
        {camps.map((cm) => (
          <Row key={cm.id}>
            <div><p className="text-sm text-white">{cm.topic}</p><p className="text-xs text-muted-foreground">كل {cm.frequency_hours} ساعة · {(cm.platforms || []).length} منصات · الموعد التالي: {cm.next_run || "غير محدد"}</p><p className="text-xs text-muted-foreground">آخر نتيجة: {cm.last_run_status || "لم تعمل بعد"}</p></div>
            <span className={`text-xs ${cm.active ? "text-emerald-400" : "text-rose-400"}`}>{cm.active ? "نشطة" : "متوقفة"}</span>
            <div className="flex gap-2">
              <Button onClick={() => runCampaign(cm.id)} disabled={busy} variant="outline" className="h-8 rounded-lg border-cyan/30 bg-transparent px-3 text-xs text-cyan hover:bg-cyan/10"><Send className="h-3 w-3" /> تشغيل</Button>
              <Button onClick={() => api.adminToggleCampaign(cm.id, pin).then(load)} variant="outline" className="h-8 rounded-lg border-white/15 bg-transparent px-3 text-xs text-white hover:bg-white/5"><Power className="h-3 w-3" /></Button>
              <Button onClick={() => api.adminDeleteCampaign(cm.id, pin).then(load)} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
        {!camps.length && <p className="py-4 text-center text-sm text-muted-foreground">لا توجد حملات.</p>}
      </div>

      <h3 className="mb-2 font-semibold text-white">آخر المنشورات</h3>
      <div className="space-y-2">
        {posts.slice(0, 12).map((p) => (
          <Row key={p.id}>
            <span className="text-sm text-white">{p.topic}</span>
            <span className="text-xs text-muted-foreground">{(p.platforms || []).length} منصات</span>
            <span className={`rounded-md px-2 py-0.5 text-xs ${p.status === "published" || p.status === "completed" ? "bg-emerald-400/10 text-emerald-400" : "bg-amber-400/10 text-amber-300"}`}>{p.status || (p.auto ? "تلقائي" : "مسودة")}</span>
            {p.status !== "published" && p.status !== "completed" && <Button onClick={() => publishPost(p.id)} variant="outline" className="h-8 rounded-lg border-cyan/30 bg-transparent px-3 text-xs text-cyan hover:bg-cyan/10">إرسال للموصل</Button>}
          </Row>
        ))}
        {!posts.length && <p className="py-4 text-center text-sm text-muted-foreground">لا توجد منشورات بعد.</p>}
      </div>
    </Section>
  );
}

function AIStudioTab({ pin }) {
  const [f, setF] = useState({ name: "", system: "", greeting: "مرحبًا! كيف أساعدك اليوم؟", provider: "openai" });
  const [list, setList] = useState([]);
  const [url, setUrl] = useState("");
  const load = () => api.adminAgents(pin).then(setList);
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.name || !f.system) { toast.error("أدخل الاسم وتعليمات الذكاء"); return; }
    try { const r = await api.createAgent({ ...f, published: true }); setUrl(r.url); toast.success("تم إنشاء الذكاء ونشره!"); setF({ ...f, name: "", system: "" }); load(); }
    catch (e) { toast.error("خطأ"); }
  };
  return (
    <Section title="استوديو الذكاء — أنشئ ذكاءً ووكلاء خاصين بك">
      <div className="mb-4 grid gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label className="text-xs text-white">اسم الذكاء / الوكيل</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={"mt-1 " + inp} placeholder="مساعد مبيعات التمور" /></div>
          <div><Label className="text-xs text-white">المزود</Label>
            <select value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} className={"mt-1 h-10 w-full rounded-lg px-3 " + inp} dir="ltr">
              {["openai", "anthropic", "gemini"].map((p) => <option key={p} value={p} className="bg-[#0b1116]">{p}</option>)}
            </select></div>
        </div>
        <div><Label className="text-xs text-white">تعليمات الذكاء (الشخصية والمعرفة)</Label><Textarea value={f.system} onChange={(e) => setF({ ...f, system: e.target.value })} rows={5} className={"mt-1 " + inp} placeholder="أنت خبير في تمور المدينة، تجيب باحتراف وود…" /></div>
        <div><Label className="text-xs text-white">رسالة الترحيب</Label><Input value={f.greeting} onChange={(e) => setF({ ...f, greeting: e.target.value })} className={"mt-1 " + inp} /></div>
        <Button onClick={create} className="btn-cyan w-fit gap-2 rounded-lg px-6 font-semibold hover:bg-transparent"><Sparkles className="h-4 w-4" /> إنشاء ونشر</Button>
        {url && <a href={url} target="_blank" rel="noreferrer" className="font-en text-sm text-cyan" dir="ltr">{window.location.origin}{url}</a>}
      </div>
      <div className="space-y-2">
        {list.map((a) => (
          <Row key={a.id}>
            <div><p className="text-sm text-white">{a.name}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">/agent/{a.id}</p></div>
            <div className="flex gap-2">
              <a href={`/agent/${a.id}`} target="_blank" rel="noreferrer" className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/5">فتح</a>
              <Button onClick={() => api.adminDeleteAgent(a.id, pin).then(load)} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </div>
          </Row>
        ))}
      </div>
    </Section>
  );
}

function VaultTab({ pin }) {
  const [keys, setKeys] = useState([]);
  const [vault, setVault] = useState([]);
  const [gen, setGen] = useState({ label: "", scope: "all", count: 1 });
  const [ent, setEnt] = useState({ name: "", value: "", category: "عام" });
  const load = () => { api.adminApikeys(pin).then(setKeys); api.adminVault(pin).then(setVault); };
  useEffect(() => { load(); }, []);
  const generate = async () => {
    if (!gen.label) { toast.error("أدخل وصف المفتاح"); return; }
    const r = await api.adminApikeyGen(gen, pin); toast.success(`تم توليد ${r.codes.length} مفتاح خاص`); load();
  };
  const saveEntry = async () => {
    if (!ent.name || !ent.value) { toast.error("أدخل الاسم والقيمة"); return; }
    await api.adminVaultSave(ent, pin); toast.success("تم حفظ التكامل"); setEnt({ name: "", value: "", category: "عام" }); load();
  };
  return (
    <Section title="الربط والمفاتيح — ملكية كاملة">
      <div className="mb-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-1 font-semibold text-cyan">توليد رموز المنصة الداخلية (IQLX)</h3>
        <p className="mb-3 text-xs text-muted-foreground">رموز داخلية محفوظة في قاعدة المنصة، قابلة للإلغاء. لا تمنح رصيدًا أو استخدامًا غير محدود لدى مزودي الخدمات الخارجيين.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1"><Label className="text-xs text-white">وصف المفتاح</Label><Input value={gen.label} onChange={(e) => setGen({ ...gen, label: e.target.value })} className={"mt-1 " + inp} placeholder="مثال: مفتاح البناء الخام" /></div>
          <div><Label className="text-xs text-white">النطاق</Label><Input value={gen.scope} onChange={(e) => setGen({ ...gen, scope: e.target.value })} className={"mt-1 w-32 " + inp} dir="ltr" /></div>
          <div><Label className="text-xs text-white">العدد</Label><Input type="number" value={gen.count} onChange={(e) => setGen({ ...gen, count: parseInt(e.target.value || 1) })} className={"mt-1 w-20 " + inp} /></div>
          <Button onClick={generate} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> توليد</Button>
        </div>
        <div className="mt-3 space-y-2">
          {keys.map((k) => (
            <Row key={k.id}>
              <span className="font-en text-sm text-cyan" dir="ltr">{k.code}</span>
              <span className="text-xs text-muted-foreground">{k.label} · {k.scope}</span>
              <span className={`text-xs ${k.status === "active" ? "text-emerald-400" : "text-rose-400"}`}>{k.status}</span>
              {k.status === "active" && <Button onClick={() => api.adminApikeyRevoke(k.code, pin).then(load)} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10">إلغاء</Button>}
            </Row>
          ))}
          {!keys.length && <p className="py-3 text-center text-xs text-muted-foreground">لا توجد مفاتيح بعد.</p>}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <h3 className="mb-1 font-semibold text-gold">خزنة التكاملات — أضِف أي عملية ربط</h3>
        <p className="mb-3 text-xs text-muted-foreground">خزّن مفاتيح أي خدمة خارجية (fal.ai، Stripe، Twilio، أي API) وأدرها من هنا.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <div><Label className="text-xs text-white">اسم التكامل</Label><Input value={ent.name} onChange={(e) => setEnt({ ...ent, name: e.target.value })} className={"mt-1 " + inp} placeholder="fal.ai / Stripe…" /></div>
          <div><Label className="text-xs text-white">القيمة / المفتاح</Label><Input value={ent.value} onChange={(e) => setEnt({ ...ent, value: e.target.value })} className={"mt-1 " + inp} dir="ltr" /></div>
          <div><Label className="text-xs text-white">التصنيف</Label><Input value={ent.category} onChange={(e) => setEnt({ ...ent, category: e.target.value })} className={"mt-1 " + inp} /></div>
        </div>
        <Button onClick={saveEntry} className="btn-gold mt-3 gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Plus className="h-4 w-4" /> إضافة تكامل</Button>
        <div className="mt-3 space-y-2">
          {vault.map((v) => (
            <Row key={v.id}>
              <span className="text-sm text-white">{v.name} <span className="text-xs text-muted-foreground">· {v.category}</span></span>
              <span className="font-en text-xs text-muted-foreground" dir="ltr">{(v.value || "").slice(0, 6)}••••</span>
              <Button onClick={() => api.adminVaultDelete(v.id, pin).then(load)} variant="outline" className="h-8 rounded-lg border-rose-400/30 bg-transparent px-3 text-xs text-rose-300 hover:bg-rose-400/10"><Trash2 className="h-3 w-3" /></Button>
            </Row>
          ))}
          {!vault.length && <p className="py-3 text-center text-xs text-muted-foreground">لا توجد تكاملات مخزّنة.</p>}
        </div>
      </div>
    </Section>
  );
}

function AIEnginesTab({ pin }) {
  const CAPS = [
    { id: "text", label: "النص", desc: "الدردشة، الملخصات، العقود، الوكلاء" },
    { id: "image", label: "الصور", desc: "توليد الصور والشعارات" },
    { id: "audio", label: "الصوت", desc: "النظرة الصوتية / التعليق" },
  ];
  const [eng, setEng] = useState(null);
  const [testing, setTesting] = useState("");
  const load = () => api.adminAIEngines(pin).then(setEng);
  useEffect(() => { api.adminAIEngines(pin).then(setEng); }, [pin]);
  if (!eng) return <Loader2 className="h-6 w-6 animate-spin text-cyan" />;
  const set = (cap, k, v) => setEng({ ...eng, [cap]: { ...eng[cap], [k]: v } });
  const save = async (cap) => {
    const c = eng[cap];
    await api.adminSaveAIEngine({ capability: cap, mode: c.mode || "custom", base_url: c.base_url || "", model: c.model || "", region: c.region || "us-east-1", api_key: c.newKey || "" }, pin);
    toast.success("تم الحفظ"); load();
  };
  const test = async (cap) => {
    setTesting(cap);
    try { const r = await api.adminTestAIEngine(cap, pin); toast.success(r.message || "نجح"); }
    catch (e) { toast.error(e?.response?.data?.detail || "فشل الاختبار"); }
    setTesting("");
  };
  return (
    <Section title="محرّكات الذكاء — خوادمك الخاصة">
      <p className="mb-2 text-sm text-muted-foreground">يمكن تشغيل النص عبر AWS Bedrock باعتماديات IAM الخاصة بالخادم، أو عبر خادم متوافق مع OpenAI. الصور والصوت يستخدمان الخادم الخاص.</p>
      <p className="mb-5 text-sm text-muted-foreground">الفيديو يُولّد عبر تكامل fal.ai ومفتاحك الخاص في إعدادات التكاملات.</p>
      <div className="grid gap-4">
        {CAPS.map((cap) => {
          const c = eng[cap.id];
          return (
            <div key={cap.id} data-testid={`engine-card-${cap.id}`} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div><h3 className="font-semibold text-white">{cap.label}</h3><p className="text-xs text-muted-foreground">{cap.desc}</p></div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${(c.mode === "bedrock" && c.model) || (c.base_url && c.model) ? "bg-emerald-400/10 text-emerald-300" : "bg-amber-400/10 text-amber-300"}`}>
                  {(c.mode === "bedrock" && c.model) || (c.base_url && c.model) ? "مُعدّ" : "بحاجة إلى إعداد"}
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                {cap.id === "text" && <div><Label className="text-xs text-white">نوع محرّك النص</Label><select value={c.mode || "custom"} onChange={(e) => set(cap.id, "mode", e.target.value)} className={"mt-1 h-10 w-full rounded-lg px-3 " + inp} dir="ltr"><option value="custom" className="bg-[#0b1116]">OpenAI-compatible</option><option value="bedrock" className="bg-[#0b1116]">AWS Bedrock</option></select></div>}
                {cap.id === "text" && c.mode === "bedrock" ? <div><Label className="text-xs text-white">AWS Region</Label><Input value={c.region || "us-east-1"} onChange={(e) => set(cap.id, "region", e.target.value)} className={"mt-1 " + inp} dir="ltr" placeholder="us-east-1" /></div> : <div><Label className="text-xs text-white">رابط الخادم (Base URL)</Label><Input value={c.base_url || ""} onChange={(e) => set(cap.id, "base_url", e.target.value)} className={"mt-1 " + inp} dir="ltr" placeholder="http://localhost:11434/v1" /></div>}
                <div><Label className="text-xs text-white">معرّف النموذج</Label><Input value={c.model || ""} onChange={(e) => set(cap.id, "model", e.target.value)} className={"mt-1 " + inp} dir="ltr" placeholder={c.mode === "bedrock" ? "anthropic.claude-3-haiku-20240307-v1:0" : cap.id === "text" ? "qwen2.5:7b" : "model-name"} /></div>
                {!(cap.id === "text" && c.mode === "bedrock") && <div><Label className="text-xs text-white">مفتاح الخادم {c.has_key && <span className="text-emerald-400">(محفوظ)</span>}</Label><Input value={c.newKey || ""} onChange={(e) => set(cap.id, "newKey", e.target.value)} type="password" className={"mt-1 " + inp} dir="ltr" placeholder={c.has_key ? "••••" : "اختياري للخادم المحلي"} /></div>}
              </div>
              <div className="mt-3 flex gap-2">
                <Button data-testid={`engine-save-${cap.id}`} onClick={() => save(cap.id)} className="btn-cyan gap-2 rounded-lg px-5 font-semibold hover:bg-transparent"><Check className="h-4 w-4" /> حفظ</Button>
                <Button data-testid={`engine-test-${cap.id}`} onClick={() => test(cap.id)} disabled={testing === cap.id} variant="outline" className="gap-2 rounded-lg border-cyan/30 bg-transparent px-5 text-cyan hover:bg-cyan/10">{testing === cap.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} اختبار</Button>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function AdminAI({ pin }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.adminAIHistory("console", pin).then(setMsgs).catch(() => toast.error("تعذّر تحميل سجل الوكيل")); }, [pin]);
  const send = async () => {
    if (!input.trim() || loading) return;
    const t = input.trim(); setInput(""); setMsgs((m) => [...m, { role: "user", content: t }]); setLoading(true);
    try {
      const r = await api.adminAI({ message: t, service: "admin", session_id: "console" }, pin);
      setMsgs((m) => [...m, { role: "assistant", content: r.content, action: r.action }]);
      if (r.action?.ok) {
        const map = { category: "تصنيف", page: "صفحة", service: "خدمة", agent: "وكيل", project: "مسودة مشروع", integration: "تكامل" };
        toast.success(`✅ تم إنشاء ${map[r.action.type] || "عنصر"}: ${r.action.label}`);
      }
    }
    catch (e) { toast.error("خطأ"); }
    setLoading(false);
  };
  return (
    <Section title="الذكاء الإداري">
      <div className="flex h-[56vh] flex-col rounded-xl border border-white/10 bg-white/[0.02]">
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {msgs.length === 0 && <p className="pt-16 text-center text-sm text-muted-foreground">وكيل الإدارة — أنشئ مسودة موقع أو تطبيق تراجعها في استوديو البناء، أو أنشئ تصنيفًا أو صفحة أو خدمة أو وكيلًا. إعداد الدفع والبريد والنشر الخارجي يحتاج بيانات مزود موثقة.</p>}
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
              <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-white/5 text-white" : "border border-cyan/20 bg-cyan/5 text-cyan-50"}`}>
                {m.content}
                {m.action?.ok && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg bg-emerald-400/10 px-3 py-1.5 text-xs text-emerald-300">
                    <CheckCircle2 className="h-3.5 w-3.5" /> تم التنفيذ: {m.action.label}
                    {m.action.url && <a href={m.action.url} target="_blank" rel="noreferrer" className="underline">فتح</a>}
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && <div className="flex justify-end"><div className="rounded-2xl border border-cyan/20 bg-cyan/5 px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-cyan" /></div></div>}
        </div>
        <div className="flex gap-2 border-t border-white/5 p-3">
          <Textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={2} placeholder="اكتب طلبك للإدارة…" className={"resize-none " + inp} />
          <Button onClick={send} disabled={loading} className="btn-cyan h-auto rounded-xl px-4 hover:bg-transparent">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
        </div>
      </div>
    </Section>
  );
}

function AdminBuildProjects({ pin }) {
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.adminProjects(pin).then(setProjects).catch(() => toast.error("تعذّر تحميل المشاريع"));
  useEffect(() => { api.adminProjects(pin).then(setProjects).catch(() => toast.error("تعذّر تحميل المشاريع")); }, [pin]);
  const clone = async (project) => {
    setBusy(project.id);
    try {
      const copy = await api.adminCloneProject(project.id, pin);
      toast.success("تم إنشاء نسخة مسودة");
      await load();
      window.location.href = `/build?projectId=${copy.id}`;
    } catch { toast.error("تعذّر نسخ المشروع"); }
    setBusy("");
  };
  const download = async (project) => {
    setBusy(project.id);
    try {
      const blob = await api.adminDownloadProject(project.id, pin);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `aql4-project-${project.id.slice(0, 12)}.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("تعذّر تنزيل ملفات المشروع"); }
    setBusy("");
  };
  return (
    <Section title="مشاريع البناء المحفوظة">
      <div className="space-y-2">
        {projects.map((project) => (
          <Row key={project.id}>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-white">{project.name}</p>
              <p className="text-xs text-muted-foreground">{project.type} · {project.published ? "منشور" : "مسودة"}</p>
            </div>
            <a href={`/build?projectId=${project.id}`} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/5">فتح وتعديل</a>
            <Button onClick={() => clone(project)} disabled={busy === project.id} title="إنشاء نسخة" variant="outline" className="h-8 border-white/15 bg-transparent px-2 text-white hover:bg-white/5"><Copy className="h-4 w-4" /></Button>
            <Button onClick={() => download(project)} disabled={busy === project.id} title="تنزيل ملفات المشروع" variant="outline" className="h-8 border-white/15 bg-transparent px-2 text-white hover:bg-white/5"><Download className="h-4 w-4" /></Button>
          </Row>
        ))}
        {!projects.length && <p className="py-8 text-center text-sm text-muted-foreground">لا توجد مشاريع محفوظة.</p>}
      </div>
    </Section>
  );
}
